import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Field, reduxForm, getFormValues } from 'redux-form';
import { Grid, Form, Label } from 'semantic-ui-react';
import moment from 'moment';

import { VERIFICATION_CONFIGURATION_FORM_NAME } from 'Common/FormEnum';
import { ControlInfoEnum as Fields } from 'Common/FieldEnum';
import { ReduxFormButtonGroup } from 'Components/forms/ReduxFormButtonGroup';
import { DatePickerField } from 'Common/SemanticWrapper';
import VerificationInstructions from './Instructions';
import VerificationType from './VerificationType';
import { VerificationTypes } from './VerificationTypes';
import { TitledCard } from 'Components';
import { MonthEnum } from 'Common/Constants/Enumeration/MonthEnum';
import { defaultDateFormat } from 'Common/GeneralHelper';
import { getFiscalYearStart, getFiscalYearEnd } from 'State/selectors';
import { getControlInfo } from 'State/controlInfo/actions';

import 'Styles/pages/verificationconfiguration/input.less';

class VerificationConfigurationForm extends Component {
  defaultApplicationFileAsOfDate = defaultDateFormat(
    moment({ month: MonthEnum.October, day: 1 })
  );
  defaultStudentFileAsOfDate = defaultDateFormat(
    moment({ month: MonthEnum.October, day: 31 })
  );

  state = {
    asOfRecommendedByUSDAErrorIsVisible: false,
    applicationAsOfsMessage: false,
    studentsAsOfsMessage: false,
  };

  verificationConfigurationTypeOptions = [
    { value: VerificationTypes.standard, label: 'Standard (Error Prone)' },
    { value: VerificationTypes.alternate1, label: 'Alternate 1 (Random)' },
    {
      value: VerificationTypes.alternate2,
      label: 'Alternate 2 (Error Prone and Case Number)',
    },
  ];

  applicationPlaceHolder = () => {
    return `e.g. ${defaultDateFormat(
      moment({ month: MonthEnum.October, day: 1 })
    )}`;
  };

  studentPlaceHolder = () => {
    return `e.g. ${defaultDateFormat(
      moment({ month: MonthEnum.October, day: 31 })
    )}`;
  };

  errorLabel = (name, children) => {
    return (
      <Label style={{ color: 'red' }} name={name}>
        {children}
      </Label>
    );
  };

  renderAsOfRecommendedByUSDAError = () => {
    return !this.state.asOfRecommendedByUSDAErrorIsVisible
      ? null
      : this.errorLabel(
          Fields.AsOfDateRecommendedByUSDA,
          "You are changing the 'as of' date recommended by the USDA"
        );
  };

  validateAsOfValue = (label) => (value) => {
    const currentValue = moment(value, 'MM-DD-YYYY');
    const isOutOfRange =
      currentValue.isBefore(this.props.fiscalYearStart) ||
      currentValue.isAfter(this.props.fiscalYearEnd);
    return !value || !currentValue.isValid()
      ? `${label} As Of Date is required`
      : isOutOfRange
      ? `Enter a date in the current fiscal year`
      : false;
  };

  asOfDateRequired = (value) =>
    !value || !moment(value, 'MM-DD-YYYY').isValid();
  asOfDateInFiscalYear = (value) => {
    const currentValue = moment(value, 'MM-DD-YYYY');
    return (
      currentValue.isBefore(this.props.fiscalYearStart) ||
      currentValue.isAfter(this.props.fiscalYearEnd)
    );
  };

  onApplicationAsOfFieldChange = (e, data) => {
    const { formValues } = this.props;

    if (data !== this.defaultApplicationFileAsOfDate) {
      this.setState({ asOfRecommendedByUSDAErrorIsVisible: true });
    } else if (
      formValues[Fields.CountApplicationAsOf] ===
        this.defaultApplicationFileAsOfDate &&
      formValues[Fields.CountStudentAsOf] === this.defaultStudentFileAsOfDate
    ) {
      this.setState({ asOfRecommendedByUSDAErrorIsVisible: false });
    }
    const applicationAsOfs = this.validateAsOfValue('Application');
    this.setState({ applicationAsOfsMessage: applicationAsOfs(data) });
  };

  onStudentAsOfFieldChange = (e, data) => {
    const { formValues } = this.props;
    if (data !== this.defaultStudentFileAsOfDate) {
      this.setState({ asOfRecommendedByUSDAErrorIsVisible: true });
    } else if (
      formValues[Fields.CountApplicationAsOf] ===
        this.defaultApplicationFileAsOfDate &&
      formValues[Fields.CountStudentAsOf] === this.defaultStudentFileAsOfDate
    ) {
      this.setState({ asOfRecommendedByUSDAErrorIsVisible: false });
    }
    const studentsAsOfs = this.validateAsOfValue('Student');
    this.setState({ studentsAsOfsMessage: studentsAsOfs(data) });
  };

  componentDidMount() {
    const { getControlInfo } = this.props;
    getControlInfo();
  }

  render() {
    const { handleSubmit, dispatch, disabled, formValues } = this.props;

    const { applicationAsOfsMessage, studentsAsOfsMessage } = this.state;

    return (
      <Form onSubmit={handleSubmit}>
        <TitledCard
          title={'Capture counts on the following dates'}
          className={'verificationConfiguration'}
        >
          <Grid>
            <Grid.Row columns={3}>
              <Grid.Column width={3}>
                <Field
                  component={DatePickerField}
                  label={'Applications on file as of'}
                  name={Fields.CountApplicationAsOf}
                  type={'text'}
                  placeholder={this.applicationPlaceHolder()}
                  disabled={disabled}
                  onChange={this.onApplicationAsOfFieldChange}
                  validate={[this.asOfDateRequired, this.asOfDateInFiscalYear]}
                />
              </Grid.Column>
              <Grid.Column width={4} className={'left middle aligned'}>
                {applicationAsOfsMessage &&
                  this.errorLabel(
                    Fields.CountApplicationAsOf,
                    applicationAsOfsMessage
                  )}
              </Grid.Column>
              <Grid.Column width={5} className={'left middle aligned'}>
                {this.renderAsOfRecommendedByUSDAError()}
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={3}>
              <Grid.Column width={3}>
                <Field
                  component={DatePickerField}
                  label={'Students on file as of'}
                  name={Fields.CountStudentAsOf}
                  type={'text'}
                  placeholder={this.studentPlaceHolder()}
                  disabled={disabled}
                  onChange={this.onStudentAsOfFieldChange}
                  validate={[this.asOfDateRequired, this.asOfDateInFiscalYear]}
                />
              </Grid.Column>
              <Grid.Column width={9} className={'left middle aligned'}>
                {studentsAsOfsMessage &&
                  this.errorLabel(
                    Fields.CountStudentAsOf,
                    studentsAsOfsMessage
                  )}
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </TitledCard>
        <TitledCard
          title={'Select your type of verification'}
          className={'verificationConfiguration'}
        >
          <Grid style={{ paddingTop: '8px' }}>
            <Grid.Row>
              <Grid.Column>
                <ReduxFormButtonGroup
                  dispatch={dispatch}
                  formName={VERIFICATION_CONFIGURATION_FORM_NAME}
                  fieldName={Fields.VerificationConfigurationType}
                  value={
                    formValues
                      ? formValues[Fields.VerificationConfigurationType]
                      : undefined
                  }
                  options={this.verificationConfigurationTypeOptions}
                  disabled={disabled}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column width={3}>
                <VerificationType
                  type={
                    formValues
                      ? formValues[Fields.VerificationConfigurationType]
                      : undefined
                  }
                  disabled={disabled}
                />
              </Grid.Column>
              <Grid.Column width={8} verticalAlign={'middle'}>
                <VerificationInstructions />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </TitledCard>
      </Form>
    );
  }
}

const validate = (values, props) => {
  const errors = {};
  if (!values[Fields.VerificationConfigurationType]) {
    errors[Fields.VerificationConfigurationType] =
      'Please select a verification type';
  }
  if (
    values[Fields.ErrorProneApplicationPercentage] <= 0 ||
    values[Fields.ErrorProneApplicationPercentage] > 100
  ) {
    errors[Fields.ErrorProneApplicationPercentage] =
      'Percentage must be equal to or less than 100, but greater than 0.';
  }
  if (
    values[Fields.Alt2ApplicationPercentage] <= 0 ||
    values[Fields.Alt2ApplicationPercentage] > 100
  ) {
    errors[Fields.Alt2ApplicationPercentage] =
      'Percentage must be equal to or less than 100, but greater than 0.';
  }
  if (
    values[Fields.RandomApplicationPercentage] <= 0 ||
    values[Fields.RandomApplicationPercentage] > 100
  ) {
    errors[Fields.RandomApplicationPercentage] =
      'Percentage must be equal to or less than 100, but greater than 0.';
  }
  if (
    values[Fields.Alt2CaseNumberPercentage] > 100 ||
    values[Fields.Alt2CaseNumberPercentage] <= 0
  ) {
    errors[Fields.Alt2CaseNumberPercentage] =
      'Percentage must be equal to or less than 100, but greater than 0.';
  }

  return errors;
};

const mapStateToProps = (state) => {
  return {
    fiscalYearStart: getFiscalYearStart(state),
    fiscalYearEnd: getFiscalYearEnd(state),
    formValues: getFormValues(VERIFICATION_CONFIGURATION_FORM_NAME)(state),
  };
};

const mapDispatchToProps = {
  getControlInfo: getControlInfo,
};

VerificationConfigurationForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(VerificationConfigurationForm);

export default reduxForm({
  validate,
  form: VERIFICATION_CONFIGURATION_FORM_NAME,
  enableReinitialize: true,
})(VerificationConfigurationForm);



// WEBPACK FOOTER //
// ./src/pages/verificationconfiguration/components/VerificationConfigurationForm.js